import { useEffect } from 'react';
import { blockAt } from './drag.ts';
import type { EditorApi } from './editor.tsx';
import { clearSelection, getState, setSelection } from './store.ts';

/**
 * Picking out more than one block.
 *
 * Each block is its own editable line, and a browser will not let a text
 * selection leave the line it started in — drag from the second line to the
 * sixth and you get five characters of the second, not five lines. So the
 * selection is driven from the pointer instead: once a drag leaves the block
 * it began in, the blocks between the two ends are picked out as whole
 * blocks, and the drag handle then carries all of them.
 */
export function useBlockSelection(api: EditorApi): void {
  useEffect(() => {
    let anchorId: string | null = null;
    let armed = false;   // the button went down somewhere a drag can start
    let active = false;  // the drag has left its first block
    let startY = 0;

    const pickRange = (to: string) => {
      if (!anchorId) return;
      const order = api.flat.map((b) => b.id);
      const a = order.indexOf(anchorId);
      const b = order.indexOf(to);
      if (a < 0 || b < 0) return;
      setSelection(order.slice(Math.min(a, b), Math.max(a, b) + 1));
    };

    const stop = () => {
      armed = false;
      active = false;
      document.body.classList.remove('picking');
    };

    const onDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      const target = e.target as Element | null;
      // A drag of the selection starts on a handle, so that one keeps it.
      if (target?.closest('.bgutter')) return;

      clearSelection();
      if (!target?.closest('.page-blocks')) { stop(); return; }

      armed = true;
      active = false;
      startY = e.clientY;
      anchorId = blockAt(e.clientX, e.clientY);
    };

    const onMove = (e: MouseEvent) => {
      if (!armed || !anchorId) return;

      if (!active) {
        // A short wobble inside one line is someone selecting words.
        if (Math.abs(e.clientY - startY) < 8) return;
        const here = blockAt(e.clientX, e.clientY);
        if (!here || here === anchorId) return;
        active = true;
        document.body.classList.add('picking');
      }

      const here = blockAt(e.clientX, e.clientY);
      if (here) pickRange(here);
      // The half-selected words in the first line would otherwise stay lit.
      document.getSelection()?.removeAllRanges();
      e.preventDefault();
    };

    const onKeyDown = (e: KeyboardEvent) => {
      const ids = getState().selection;
      if (!ids.length) return;

      if (e.key === 'Escape') { e.preventDefault(); clearSelection(); return; }

      if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault();
        api.removeMany(ids);
        clearSelection();
        return;
      }

      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        setSelection(api.flat.map((b) => b.id));
        return;
      }

      // Moving the caret means the person is done with the selection.
      if (e.key.startsWith('Arrow') || e.key === 'Enter' || e.key === 'Tab') clearSelection();
    };

    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('mousemove', onMove, true);
    document.addEventListener('mouseup', stop, true);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('mousemove', onMove, true);
      document.removeEventListener('mouseup', stop, true);
      document.removeEventListener('keydown', onKeyDown, true);
      document.body.classList.remove('picking');
    };
  }, [api]);
}
